import type { Answers, QolImpact, TSuppressionProfile } from "./_schema";
import { deriveIgniteProfile, type ExtraSignals } from "./ignite-profile";

/**
 * Sleep-quiz deriver. Wraps `deriveIgniteProfile` (the cortisol-quiz pattern)
 * with four additions:
 *
 *   1. Answer translation: `age` and `when-started` are ignite-verbatim
 *      passthroughs; three sleep answers have a genuine ignite construct match
 *      and are translated so they feed severity AND root causes ORGANICALLY
 *      (see the maps below).
 *   2. Sign severity: the 8 sleep-only sign questions feed severity via the
 *      `extraSignals` seam (worst answer -> tier1, next -> tier2/tier3, the
 *      quiz-v7 cohort-gradient convention).
 *   3. Forced primary: `nocturnal-cortisol` leads the verdict on every path via
 *      `forcePrimaryCause`. The organic top scorer stays visible as Secondary.
 *   4. QoL floor: the tolerance check (`sleep-cost`) is a direct self-report of
 *      functional impact, which `deriveQolImpact` cannot see from this quiz's
 *      answers -- without the floor, `high` is unreachable on every path.
 *
 * Why a NEW root cause rather than reusing `sleep-deprivation`: this quiz's
 * entire premise is that DURATION is not the problem ("you're in bed the right
 * number of hours"), and the base `sleep-deprivation` copy reads "your sleep
 * window is too short" -- it would contradict the quiz on its own payoff screen.
 * And rather than reusing `cortisol-dominance` (the cortisol quiz's forced
 * cause): with that forced, the organic secondary lands on `chronic-stress` on
 * many paths, producing a sleep quiz whose verdict grid never mentions sleep.
 *
 * On-angle guard: nothing is translated to `body-composition`,
 * `processed-foods`, or `family-history`, so `visceral-adiposity`,
 * `metabolic-syndrome`, `overtraining`, and `endocrine-disruption` are
 * unscoreable and the organic secondary can only ever surface on-angle
 * (cortisol/chronic-stress, sleep-deprivation, lifestyle-load,
 * age-related-decline). Verified across the full 16,777,216-path space in
 * tests/quizzes/quiz-sleep.test.ts.
 *
 * Pure function of `answers` -- reproducible server-side from the persisted
 * submission (rule 11).
 */

/** Ids whose values are ignite-verbatim -- copied through untranslated. */
const PASSTHROUGH_IDS = ["age", "when-started"] as const;

/**
 * `wake-pattern` -> ignite `sleep-hours`. Translation, not manufacture: waking at
 * 2-3 AM and not getting back down IS a short *effective* night, whatever time
 * the user went to bed. Same idiom as quiz-energy-profile's SLEEP_TO_HOURS.
 * This is what makes `sleep-deprivation` scoreable as the organic Secondary.
 */
const WAKE_TO_HOURS: Record<string, "lt-5" | "5-6" | "7-8"> = {
  "most-nights": "lt-5",
  "few-times-week": "5-6",
  rarely: "7-8",
  never: "7-8",
};

/**
 * `wired-tired` -> ignite `stress-symptoms`. Genuine construct match: the ignite
 * stress question is "tired, cranky, foggy, or moody", and "wiped out but too
 * wired to fall asleep" is the same autonomic over-arousal in sleep language.
 */
const WIRED_TO_STRESS: Record<string, "yes" | "no"> = {
  "almost-every-night": "yes",
  "few-nights-week": "yes",
  occasionally: "no",
  rarely: "no",
};

/**
 * Aspiration beat -> ignite `results-want` (expected-outcome only, unrendered).
 * Deliberately NOT also mapped to `concern`: `concern === "sex-drive"` adds a
 * tier1 point in `countSignals` on top of the direct `libido` question, which
 * would double-count the same signal.
 */
const GOAL_TO_RESULTS: Record<string, "energy" | "sex-drive" | "focus" | "all-of-above"> = {
  energy: "energy",
  drive: "sex-drive",
  focus: "focus",
  all: "all-of-above",
};

/**
 * Sign-question severity mapping. `wake-pattern` and `wired-tired` are absent by
 * design -- they already feed severity organically through the translations
 * above, and a signal must never be counted twice.
 *
 * tier1 holds three DISTINCT constructs: unrefreshing sleep, libido, and
 * functional cost. `morning-state` sits at tier2 rather than tier1 because it is
 * the intensity companion of `unrefreshed` (same construct), so promoting it
 * would double-count morning exhaustion at the top tier.
 */
const TIER1_SIGNS: Record<string, readonly string[]> = {
  unrefreshed: ["almost-every-morning"],
  libido: ["significantly"],
  "sleep-cost": ["running-my-life"],
};
const TIER2_SIGNS: Record<string, readonly string[]> = {
  unrefreshed: ["most-mornings"],
  libido: ["somewhat"],
  "sleep-cost": ["more-than-id-admit"],
  "morning-state": ["wrecked"],
  "dream-recall": ["cant-remember"],
  "sleep-aids": ["barely-help"],
};
const TIER3_SIGNS: Record<string, readonly string[]> = {
  "morning-state": ["groggy"],
  "dream-recall": ["much-less"],
  snoring: ["noticeably-worse"],
  "sleep-cost": ["some-days"],
  "sleep-aids": ["mixed"],
};

/**
 * The tolerance check is literally a quality-of-life question, so it sets the
 * QoL floor directly. The two milder answers set no floor and leave the derived
 * value alone.
 */
const COST_TO_QOL_FLOOR: Record<string, QolImpact> = {
  "running-my-life": "high",
  "more-than-id-admit": "medium",
};

function countTier(answers: Answers, map: Record<string, readonly string[]>): number {
  let n = 0;
  for (const [id, values] of Object.entries(map)) {
    const v = answers[id];
    if (typeof v === "string" && values.includes(v)) n += 1;
  }
  return n;
}

function signSignals(answers: Answers): ExtraSignals {
  return {
    tier1: countTier(answers, TIER1_SIGNS),
    tier2: countTier(answers, TIER2_SIGNS),
    tier3: countTier(answers, TIER3_SIGNS),
  };
}

function qolFloorFor(answers: Answers): QolImpact | undefined {
  const cost = answers["sleep-cost"];
  if (typeof cost !== "string") return undefined;
  return COST_TO_QOL_FLOOR[cost];
}

/**
 * Builds the ignite-namespace answer set the base deriver reads: two passthrough
 * ids plus the three genuine construct translations. No fabricated symptom
 * answers -- the sleep-specific sign severity comes from `extraSignals`.
 */
function syntheticAnswers(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  const wake = answers["wake-pattern"];
  if (typeof wake === "string" && wake in WAKE_TO_HOURS) {
    out["sleep-hours"] = WAKE_TO_HOURS[wake]!;
  }
  const wired = answers["wired-tired"];
  if (typeof wired === "string" && wired in WIRED_TO_STRESS) {
    out["stress-symptoms"] = WIRED_TO_STRESS[wired]!;
  }
  const goal = answers["sleep-goal"];
  if (typeof goal === "string" && goal in GOAL_TO_RESULTS) {
    out["results-want"] = GOAL_TO_RESULTS[goal]!;
  }
  return out;
}

export function deriveSleepProfile(answers: Answers): TSuppressionProfile {
  const profile = deriveIgniteProfile(syntheticAnswers(answers), {
    extraSignals: signSignals(answers),
    forcePrimaryCause: "nocturnal-cortisol",
    qolFloor: qolFloorFor(answers),
  });
  // Drives the sleep-specific eyebrow / subline / projection subhead / offer
  // headline via COHORT_OVERLAYS (quizzes/quiz-v7.ts).
  return { ...profile, cohort: "sleep" };
}
